import { storyblokDataAttributes } from "@commerce-frontend/storyblok-preview/component";
import { graphql } from "@commerce-frontend/types";
import { Button } from "@commerce-frontend/ui/base/Button";
import { cn } from "@commerce-frontend/ui/helpers/styles";
import { Link } from "@commerce-frontend/ui/primitives/Link";
import { BREAKPOINTS } from "@commerce-frontend/ui/tailwind.config";
import type { ResultOf } from "@graphql-typed-document-node/core";
import { ChevronRight } from "lucide-react";
import { useTranslations } from "next-intl";
import type { HTMLAttributes } from "react";
import { AssetImage } from "~/components/AssetImage/AssetImage";

const TeaserRowFragment = graphql(/* GraphQL */ `
	fragment TeaserRowFragment on TeaserRow {
		__typename
		id
		title
		viewMoreLink {
			url
			newTab
		}
		items {
			__typename
			id
			content
			image {
				...ImageAsset
			}
			link {
				url
			}
			title
		}
	}
`);

type Props = {
	data: ResultOf<typeof TeaserRowFragment>;
} & HTMLAttributes<HTMLDivElement>;

export const TeaserRow = ({ className, data }: Props) => {
	const t = useTranslations("TeaserRow");
	const { title, viewMoreLink } = data;

	return (
		<section className={cn("relative my-6 md:my-8", className)} {...storyblokDataAttributes(data)}>
			<div className="lg:container">
				<div className="container mb-4 flex items-center md:mb-6 lg:px-0">
					<h2 className="mb-0">{title}</h2>
					{viewMoreLink?.url ? (
						<Button className="ml-auto" variant="secondary" href={viewMoreLink.url}>
							{t("view-more")}
						</Button>
					) : undefined}
				</div>

				<div className="hide-scrollbar flex snap-x snap-mandatory gap-4 overflow-x-auto overflow-y-hidden scroll-smooth px-4 [scrollbar-width:none] md:gap-6 md:px-5 lg:gap-8 lg:overflow-visible lg:px-0 2xl:gap-12">
					{data.items.map((slide, index) => (
						<article
							key={index}
							aria-label={slide.title}
							className="group relative flex min-w-[80%] basis-0 scale-100 snap-center flex-col overflow-hidden transition-all md:min-w-[70%] lg:min-w-0 lg:grow lg:hover:scale-105"
							{...storyblokDataAttributes(slide)}
						>
							<Link href={slide.link?.url} aria-label={slide.title}>
								{slide.image?.filename ? (
									<AssetImage
										data={slide.image}
										sizes={`(min-width: ${BREAKPOINTS.md}) 510px, (min-width: ${BREAKPOINTS.lg}) 300px, (min-width: ${BREAKPOINTS.xl}) 390px, 275px`}
										className="aspect-[3/2] w-full object-cover md:aspect-[5/4]"
									/>
								) : undefined}

								<div className="flex grow flex-col p-6 pb-14 md:p-8 md:pb-20">
									<h3>{slide.title}</h3>

									<p className="mt-1">{slide.content}</p>

									<span className="absolute bottom-4 right-4 flex size-8 items-center justify-center rounded-full border-2 border-brand align-middle text-brand transition-colors group-hover:bg-brand group-hover:text-white md:size-12">
										<ChevronRight />
									</span>
								</div>
							</Link>
						</article>
					))}
				</div>
			</div>
		</section>
	);
};
